import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { FacebookIcon, InstagramIcon, LinkedInIcon } from './PlatformIcon';
import { Capacitor } from '@capacitor/core';
import toast from 'react-hot-toast';
import { useBusiness } from '../contexts/BusinessContext';
import { SOCIAL_ENABLED } from '../lib/featureFlags';
import { buildSocialOauthReturnUrl, oauthReturnPath } from '../lib/nativeAppScheme';
import { supabase } from '../lib/supabaseClient';
import { invokeErrorMessage } from '../lib/supabaseFunctionError';
import {
  ensureSocialConnectionSeat,
  getSocialConnectionUsage,
} from '../lib/socialConnectionSeats';
import SocialConnectionPurchaseModal from './SocialConnectionPurchaseModal';

/** Networks that use the Ayrshare connect and posting flow. */
export type SocialPlatform = 'facebook' | 'instagram' | 'linkedin';

/** @deprecated Use SocialPlatform. Kept so existing imports stay valid. */
export type ConnectSocialPlatform = SocialPlatform;

const LINKING_KEY = 'social_ayrshare_linking';
const PLATFORM_KEY = 'social_connect_platform';
const BUSINESS_KEY = 'social_connect_business_id';

interface SocialConnectButtonProps {
  platform: ConnectSocialPlatform;
  size?: 'small' | 'medium' | 'large';
  className?: string;
  hideStatusIndicator?: boolean;
  onConnectionChange?: () => void;
  buttonLabel?: string;
  /** Start linking as soon as the button is ready. Skipped on an OAuth return. */
  autoStart?: boolean;
}

export function socialConnectDescription(platform: ConnectSocialPlatform): string {
  if (platform === 'instagram') {
    return 'Use an Instagram Business or Creator account linked to a Facebook Page. After connecting, configure auto-posts in Media Manager → Automate.';
  }
  if (platform === 'facebook') {
    return 'Connect the Facebook Page you want to post from. After connecting, configure auto-posts in Media Manager → Automate.';
  }
  return 'Sign in with LinkedIn and choose a personal profile or a company page. A company page needs a Super Admin or Content Admin. After connecting, publish from Marketing.';
}

function platformMeta(platform: ConnectSocialPlatform) {
  if (platform === 'facebook') {
    return {
      label: 'Facebook',
      Icon: FacebookIcon,
      connectLabel: 'Connect Facebook',
      connectedToast: 'Facebook connected!',
    };
  }
  if (platform === 'linkedin') {
    return {
      label: 'LinkedIn',
      Icon: LinkedInIcon,
      connectLabel: 'Connect LinkedIn',
      connectedToast: 'LinkedIn connected!',
    };
  }
  return {
    label: 'Instagram',
    Icon: InstagramIcon,
    connectLabel: 'Connect Instagram',
    connectedToast: 'Instagram connected!',
  };
}

export default function SocialConnectButton({
  platform,
  size = 'medium',
  className = '',
  hideStatusIndicator = false,
  onConnectionChange,
  buttonLabel,
  autoStart = false,
}: SocialConnectButtonProps) {
  const { selectedBusiness: business } = useBusiness();
  const meta = platformMeta(platform);
  const [loading, setLoading] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [connected, setConnected] = useState(false);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [checking, setChecking] = useState(true);
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const autoStartRef = useRef(false);

  const checkConnection = useCallback(async () => {
    if (!SOCIAL_ENABLED || !business?.id) {
      setChecking(false);
      setConnected(false);
      return;
    }
    try {
      const { data } = await supabase
        .from('social_media_connections')
        .select('id, display_name, username, is_active')
        .eq('business_id', business.id)
        .eq('platform', platform)
        .eq('provider', 'ayrshare')
        .eq('is_active', true)
        .limit(1)
        .maybeSingle();
      setConnected(!!data);
      setDisplayName(data?.display_name || data?.username || null);
    } catch (err) {
      console.error(err);
    } finally {
      setChecking(false);
    }
  }, [business?.id, platform]);

  const finalizeConnection = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!business?.id) return false;
      setFinalizing(true);
      try {
        const { data, error } = await supabase.functions.invoke('ayrshare-social-sync-connection', {
          body: { businessId: business.id, platform },
        });
        if (error || data?.error) {
          const msg =
            (await invokeErrorMessage(error, data)) ||
            `Failed to finish the ${meta.label} connection.`;
          if (!opts?.silent) toast.error(msg);
          return false;
        }
        if (!data?.linked) {
          if (!opts?.silent) {
            toast.error(
              data?.message ||
                `${meta.label} was not linked. Click Connect and finish sign-in.`,
            );
          }
          return false;
        }
        try {
          sessionStorage.removeItem(LINKING_KEY);
          sessionStorage.removeItem(PLATFORM_KEY);
          sessionStorage.removeItem(BUSINESS_KEY);
        } catch {
          /* ignore */
        }
        toast.success(meta.connectedToast);
        await checkConnection();
        onConnectionChange?.();
        return true;
      } catch (e) {
        console.error(e);
        if (!opts?.silent) toast.error(`Failed to finalize ${meta.label} connection.`);
        return false;
      } finally {
        setFinalizing(false);
      }
    },
    [business?.id, checkConnection, meta.connectedToast, meta.label, onConnectionChange, platform],
  );

  useEffect(() => {
    void checkConnection();
  }, [checkConnection]);

  useEffect(() => {
    if (!business?.id || connected) return;
    const urlParams = new URLSearchParams(window.location.search);
    const returnedViaUrl =
      urlParams.get('social_oauth') === '1' && urlParams.get('platform') === platform;
    let linking = false;
    let storedPlatform = '';
    try {
      linking = sessionStorage.getItem(LINKING_KEY) === '1';
      storedPlatform = sessionStorage.getItem(PLATFORM_KEY) || '';
    } catch {
      /* ignore */
    }
    if (!returnedViaUrl && !(linking && storedPlatform === platform)) return;

    const storedBusinessId = (() => {
      try {
        return sessionStorage.getItem(BUSINESS_KEY) || '';
      } catch {
        return '';
      }
    })();
    if (storedBusinessId && storedBusinessId !== business.id) return;

    void (async () => {
      const ok = await finalizeConnection({ silent: !returnedViaUrl });
      if (ok || returnedViaUrl) {
        window.history.replaceState({}, '', window.location.pathname + window.location.hash);
      }
      if (!ok && !returnedViaUrl) {
        try {
          sessionStorage.removeItem(LINKING_KEY);
        } catch {
          /* ignore */
        }
      }
    })();
  }, [business?.id, connected, finalizeConnection, platform]);

  useEffect(() => {
    if (!business?.id) return;
    const onFocus = () => {
      let linking = false;
      let storedPlatform = '';
      try {
        linking = sessionStorage.getItem(LINKING_KEY) === '1';
        storedPlatform = sessionStorage.getItem(PLATFORM_KEY) || '';
      } catch {
        /* ignore */
      }
      if (linking && storedPlatform === platform && !connected) {
        void finalizeConnection({ silent: true });
      }
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [business?.id, connected, finalizeConnection, platform]);

  const runConnect = async () => {
    if (!business?.id) return;
    setLoading(true);
    try {
      await supabase.functions.invoke('ayrshare-social-disconnect', {
        body: { businessId: business.id, platform, action: 'reset' },
      });

      sessionStorage.setItem(BUSINESS_KEY, business.id);
      sessionStorage.setItem(PLATFORM_KEY, platform);
      sessionStorage.setItem(LINKING_KEY, '1');

      const returnUrl = buildSocialOauthReturnUrl({
        platform,
        nextPath: oauthReturnPath(),
      });

      const { data, error } = await supabase.functions.invoke('ayrshare-social-connect', {
        body: { businessId: business.id, platform, returnUrl },
      });
      if (error || data?.error) {
        toast.error((await invokeErrorMessage(error, data)) || `Failed to start ${meta.label} connect`);
        return;
      }
      if (data?.connected) {
        toast.success(meta.connectedToast);
        await checkConnection();
        onConnectionChange?.();
        return;
      }
      if (data?.authUrl) {
        if (Capacitor.isNativePlatform()) {
          window.open(data.authUrl, '_blank');
        } else {
          // Do not use noopener — Ayrshare's origin=true redirect needs window.opener.
          const popup = window.open(data.authUrl, 'ayrshare_social_link');
          if (!popup) {
            window.location.href = data.authUrl;
          }
        }
        return;
      }
      toast.error('No linking URL returned');
    } catch (err) {
      console.error(err);
      toast.error(`Failed to connect ${meta.label}`);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    if (!business?.id) return;
    setLoading(true);
    try {
      const usage = await getSocialConnectionUsage(business.id);
      if (usage && !usage.can_add) {
        setPurchaseOpen(true);
        return;
      }
      await runConnect();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!autoStart || autoStartRef.current) return;
    if (!business?.id || checking || connected) return;
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('social_oauth') === '1') return;
    autoStartRef.current = true;
    void handleConnect();
  }, [autoStart, business?.id, checking, connected]);

  const handlePurchaseConfirm = async () => {
    if (!business?.id) return;
    setPurchasing(true);
    try {
      const seat = await ensureSocialConnectionSeat(business.id);
      if (!seat.success) {
        toast.error(seat.error);
        return;
      }
      if (seat.purchased) {
        toast.success('Social connection added to your subscription');
      }
      setPurchaseOpen(false);
      await runConnect();
    } finally {
      setPurchasing(false);
    }
  };

  const handleDisconnect = async () => {
    if (!business?.id) return;
    if (!window.confirm(`Disconnect ${meta.label}? Auto-posts for this channel will stop.`)) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('ayrshare-social-disconnect', {
        body: { businessId: business.id, platform, action: 'disconnect' },
      });
      if (error || data?.error) {
        toast.error((await invokeErrorMessage(error, data)) || 'Disconnect failed');
        return;
      }
      toast.success(`${meta.label} disconnected`);
      setConnected(false);
      setDisplayName(null);
      onConnectionChange?.();
    } catch (err) {
      console.error(err);
      toast.error('Disconnect failed');
    } finally {
      setLoading(false);
    }
  };

  if (!SOCIAL_ENABLED) return null;

  const busy = loading || finalizing || checking;
  const connecting = loading || finalizing;
  const pad =
    size === 'small' ? 'px-3 py-1.5 text-xs' : size === 'large' ? 'px-5 py-3 text-sm' : 'px-4 py-2 text-sm';
  const Icon = meta.Icon;

  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <div className="flex items-center gap-2">
        <SocialConnectionPurchaseModal
          open={purchaseOpen}
          platformLabel={meta.label}
          busy={purchasing || loading}
          onCancel={() => setPurchaseOpen(false)}
          onConfirm={() => void handlePurchaseConfirm()}
        />
        {!hideStatusIndicator && (
          <span
            className={`h-2 w-2 rounded-full shrink-0 ${connected ? 'bg-green-500' : 'bg-red-500'}`}
            aria-hidden
          />
        )}
        {connected ? (
          <div className="flex items-center gap-2 min-w-0">
            {displayName && (
              <span className="text-xs text-gray-500 truncate max-w-[140px]">{displayName}</span>
            )}
            <button
              type="button"
              onClick={() => void handleDisconnect()}
              disabled={busy}
              className={`${pad} rounded-xl border border-gray-200 text-gray-700 font-semibold hover:bg-gray-50 disabled:opacity-50 inline-flex items-center gap-1.5`}
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icon className="h-3.5 w-3.5" />}
              Disconnect
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => void handleConnect()}
            disabled={busy}
            className={`${pad} rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-1.5`}
          >
            {connecting ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                {finalizing ? 'Finishing…' : 'Connecting…'}
              </>
            ) : (
              <>
                {busy ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Icon className="h-3.5 w-3.5" />
                )}
                {buttonLabel || meta.connectLabel}
              </>
            )}
          </button>
        )}
      </div>
      {connecting ? (
        <p className="text-[11px] text-gray-500 pl-4 sm:pl-5 leading-tight">
          This may take a moment…
        </p>
      ) : null}
    </div>
  );
}
